import { useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

export interface IShiftOverride {
  preferredSpecialty: string;
  isAssistedHygiene?: boolean;
  arrivalTime: string;
  departureTime: string;
  mealBreakDuration: string;
  isMealBreakPaid: boolean;
  rate: number | null;
  procedures: string[];
  /**
   * Per-day auto-confirm. Nullish means "no override for this day" — the payload
   * resolves it with `??`, so the day falls back to the form-level selection.
   */
  autoConfirm?: { favorites: boolean; eligibleProfessionals: boolean };
}

export interface IShiftDetailsFormValues {
  dates: Date[];
  officeId: string;
  professionType: string;
  preferredSpecialty: string;
  isAssistedHygiene?: boolean;
  arrivalTime: string;
  departureTime: string;
  mealBreakDuration: string;
  isMealBreakPaid: boolean;
  rate: number | null;
  /**
   * The recommended rate the Shift Details step prefilled for this request, sent on every
   * shift so the backend can store it (GOTU-7584). Request-level: per-day overrides never
   * change it. `null` means nothing was prefilled. Written by the Shift Details form when it
   * prefills `rate`; cleared by `ShiftRequestLayout` when the office or profession changes.
   */
  recommendedRate: number | null;
  procedures: string[];
  invite: string[];
  inviteNames: Record<string, string>;
  inviteSource: string;
  inviteCategory?: 'favorites' | 'worked_with' | 'new_to_you';
  autoConfirm: { favorites: boolean; eligibleProfessionals: boolean };
  shiftOverrides: Record<string, IShiftOverride>;
}

export const useShiftDetailsForm = () => {
  const { control, watch, setValue, getValues, handleSubmit } =
    useFormContext<IShiftDetailsFormValues>();

  const [openField, setOpenField] = useState<string | null>(null);

  const toggleField = useCallback(
    (fieldName: string) => {
      setOpenField((current) => (current === fieldName ? null : fieldName));
    },
    [],
  );

  return {
    control,
    watch,
    setValue,
    getValues,
    openField,
    toggleField,
    handleSubmit,
  };
};
